import { LOCALE_COOKIE_NAME } from "@/src/shared/i18n/localeCookie";
import { resolveRequestLocale } from "@smartmanager/shell-ui/shared/i18n/requestLocale";

/**
 * The landing page ("/") is prerendered once per locale x device under
 * /landing-variant/<locale>/<device> (app/landing-variant). The proxy picks the variant with
 * exactly the inputs the dynamic render used to read, so every visitor gets
 * the same HTML they got before, now served from the static cache.
 */
export const LANDING_DEVICES = ["desktop", "mobile"] as const;
export type LandingDevice = (typeof LANDING_DEVICES)[number];

export const LANDING_VARIANT_BASE_PATH = "/landing-variant";

export const MOBILE_USER_AGENT =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|Mobile|\bwv\b/i;

type HeaderSource = Pick<Headers, "get">;

export function resolveLandingDevice(headers: HeaderSource): LandingDevice {
  if (headers.get("sec-ch-ua-mobile") === "?1") return "mobile";
  return MOBILE_USER_AGENT.test(headers.get("user-agent") ?? "") ? "mobile" : "desktop";
}

/** Mirrors Next's request cookie parser: last duplicate wins, undecodable values are skipped. */
export function readRequestCookie(cookieHeader: string | null, name: string): string | undefined {
  if (!cookieHeader) return undefined;
  let value: string | undefined;
  for (const pair of cookieHeader.split(/; */)) {
    if (!pair) continue;
    const splitAt = pair.indexOf("=");
    if (splitAt === -1) {
      if (pair === name) value = "true";
      continue;
    }
    if (pair.slice(0, splitAt) !== name) continue;
    try {
      value = decodeURIComponent(pair.slice(splitAt + 1));
    } catch {
      // Next drops cookies it cannot decode; keep the previous value.
    }
  }
  return value;
}

export function resolveLandingLocale(headers: HeaderSource): string {
  return resolveRequestLocale({
    cookieLocale: readRequestCookie(headers.get("cookie"), LOCALE_COOKIE_NAME),
    acceptLanguage: headers.get("accept-language"),
  });
}

export function resolveLandingVariantPath(headers: HeaderSource): string {
  return `${LANDING_VARIANT_BASE_PATH}/${resolveLandingLocale(headers)}/${resolveLandingDevice(headers)}`;
}

export function isLandingVariantPath(pathname: string): boolean {
  return pathname === LANDING_VARIANT_BASE_PATH || pathname.startsWith(`${LANDING_VARIANT_BASE_PATH}/`);
}

export function isLandingDevice(value: string): value is LandingDevice {
  return (LANDING_DEVICES as readonly string[]).includes(value);
}
